import { computed, onBeforeMount, ref } from 'vue'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import {
  ADD_SPECIALTY_MODAL_NAME,
  ADD_STARTUP_MODAL_NAME,
  EDIT_AVATAR_MODAL_NAME,
  EDIT_BIOGRAPHY_MODAL_NAME,
  EDIT_POSITION_MODAL_NAME,
  EDIT_PROFESSION_TITLE_MODAL_NAME,
} from '@/shared/data/globalVariables.js'
import { ACCESS_TYPE, ROLE } from '@/shared/data/permissionSystem'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'

export const useUserProfileCompletion = () => {
  const { currentUser } = useCurrentUser()
  const { hasRole, hasAccess } = usePermissionSystem()

  const profileCompletion = ref({
    available: {
      description: 'Set your availability',
      href: '/settings/account/availabilities',
      order: 7,
    },
    avatar: {
      description: 'Add profile photo',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${EDIT_AVATAR_MODAL_NAME}`,
      order: 1,
    },
    communicationPresent: {
      description: 'Add communication tool of preference',
      href: '/settings/profile/communication',
      order: 8,
    },
    firstName: {
      description: 'Set first name',
      href: '/settings/profile/basic',
      order: 9,
    },
    lastName: {
      description: 'Set last name',
      href: '/settings/profile/basic',
      order: 10,
    },
    fromCity: {
      description: 'Where are you from? (City)',
      href: '/settings/profile/basic',
      order: 13,
    },
    fromCountry: {
      description: 'Where are you from? (Country)',
      href: '/settings/profile/basic',
      order: 14,
    },
    hourRate: {
      description: 'Set your hourly rate',
      href: '/settings/account/sessions',
      order: 18,
    },
    languageCorrectness: {
      description: 'Set your language',
      href: '/settings/profile/basic',
      order: 17,
    },
    livingInCity: {
      description: 'Where do you live? (City)',
      href: '/settings/profile/basic',
      order: 15,
    },
    livingInCountry: {
      description: 'Where do you live? (Country)',
      href: '/settings/profile/basic',
      order: 16,
    },
    name: {
      description: 'Set your display name',
      href: '/settings/profile/basic',
      order: 11,
    },
    roles: {
      description: 'Set your role',
      href: '/settings/profile/basic',
      order: 12,
    },
    specialties: {
      description: 'Add your specialties',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${ADD_SPECIALTY_MODAL_NAME}`,
      order: 4,
    },
    timezone: {
      description: 'Set your timezone',
      href: '/settings/profile/basic',
      order: 6,
    },
    aboutMe: {
      description: 'Tell us about yourself',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${EDIT_BIOGRAPHY_MODAL_NAME}`,
      order: 3,
    },
    professionTitle: {
      description: 'Set your profession title',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${
          hasRole(ROLE.MENTOR)
            ? EDIT_PROFESSION_TITLE_MODAL_NAME
            : EDIT_POSITION_MODAL_NAME
        }`,
      order: 2,
    },
    startups: {
      description: 'Add your startups',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${ADD_STARTUP_MODAL_NAME}`,
      order: 8,
    },
    bio: {
      description: 'Add your bio',
      href:
        currentUser.value.profileUrl +
        `?open-modal-name=${EDIT_BIOGRAPHY_MODAL_NAME}`,
      order: 3,
    },
  })

  const progressInPercent = computed(() => {
    const doneItems = Object.values(currentUser.value.profileCompletion).filter(
      (status) => status,
    ).length
    const todoItems = Object.keys(currentUser.value.profileCompletion).length
    return Math.round((doneItems / todoItems) * 100)
  })

  const mergedProfileCompletion = computed(() => {
    return Object.entries(currentUser.value.profileCompletion)
      .map(([element, status]) => ({
        ...profileCompletion.value[element],
        status,
      }))
      .sort((a, b) => a.order - b.order)
  })

  const firstItemToComplete = computed(() => {
    return mergedProfileCompletion.value.find(({ status }) => !status)
  })

  // A mentee who does not pay can open their own profile (MenteePolicy), so
  // the steps link there; other members without access go to the paywall.
  onBeforeMount(() => {
    if (hasAccess(ACCESS_TYPE.NONE) && !hasRole(ROLE.MENTEE)) {
      Object.keys(profileCompletion.value).forEach((key) => {
        profileCompletion.value[key].href = '/upgrade_plan/new'
      })
    }
  })

  return {
    progressInPercent,
    mergedProfileCompletion,
    firstItemToComplete,
  }
}
